import React, { useEffect, useMemo, useRef, useState } from "react";
import { CheckCheck, Download, File as FileIcon, FileText, Image as ImageIcon, Search, Send, Paperclip, ArrowLeft } from "lucide-react";
import { Card, Input, Button } from "../ui";
import { PageHeader } from "../components/PageHeader";
import { useBookings } from "../context/BookingContext";
import { fetchChatMessages, fetchPublicKey, upsertOwnPublicKey, uploadDocument } from "../services/chatService";
import { createChatSocket } from "../services/socketClient";
import { USER_API_BASE, sessionStorage } from "../services/http";
import {
  createSessionKey,
  decryptFileWithSessionKey,
  decryptSessionKey,
  decryptTextWithSessionKey,
  encryptFileWithSessionKey,
  encryptSessionKeyForPublicKey,
  encryptTextWithSessionKey,
  ensureUserKeyPair,
} from "../lib/chatCrypto";

const API_BASE = USER_API_BASE;

function formatMessageTime(value) {
  return new Date(value || new Date().toISOString()).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function isImageDocument(message) {
  const mime = String(message?.mimeType || "").toLowerCase();
  const name = String(message?.fileName || "").toLowerCase();
  return mime.startsWith("image/") || /\.(png|jpe?g|webp|gif)$/i.test(name);
}

const MessageBubble = ({ message, isOwn, onDownload, previewUrl }) => {
  const isDocument = message.messageType === "document";
  const isImageDoc = isDocument && isImageDocument(message);

  const getFileIcon = (mimeType) => {
    if (mimeType?.includes("image")) return { icon: ImageIcon, bg: "bg-purple-100", color: "text-purple-600" };
    if (mimeType?.includes("pdf")) return { icon: FileText, bg: "bg-red-100", color: "text-red-600" };
    if (mimeType?.includes("word") || mimeType?.includes("document")) return { icon: FileText, bg: "bg-blue-100", color: "text-blue-600" };
    return { icon: FileIcon, bg: "bg-slate-100", color: "text-slate-600" };
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "-";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const formatFileType = (mimeType, fileName = "") => {
    const lowerName = String(fileName || "").toLowerCase();
    if (mimeType?.includes("pdf") || lowerName.endsWith(".pdf")) return "PDF";
    if (mimeType?.includes("word") || mimeType?.includes("document") || lowerName.endsWith(".doc") || lowerName.endsWith(".docx")) return "DOC";
    if (mimeType?.includes("image") || /\.(png|jpe?g|webp|gif)$/i.test(lowerName)) return "IMAGE";
    return "FILE";
  };

  return (
    <div className={`flex ${isOwn ? "justify-end" : "justify-start"}`}>
      {isImageDoc ? (
        <div className={`max-w-[320px] rounded-2xl border shadow-md overflow-hidden ${isOwn ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200"}`}>
          <div className="relative">
            <button
              onClick={() => onDownload?.(message)}
              className="block w-full text-left"
              aria-label="Open image"
            >
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt={String(message.fileName || "Image")}
                  className="w-full h-auto max-h-[280px] object-cover"
                />
              ) : (
                <div className="w-full h-[180px] bg-slate-200 animate-pulse" />
              )}
            </button>

            <div className="absolute right-2 bottom-2 px-2 py-1 rounded-full bg-black/55 backdrop-blur-sm text-white flex items-center gap-1">
              <span className="text-[10px] leading-none">{formatMessageTime(message.timestamp)}</span>
              {isOwn ? <CheckCheck size={12} className="text-sky-300" /> : null}
            </div>
          </div>
          <div className={`px-3 py-2 flex items-center justify-between gap-2 ${isOwn ? "text-slate-200" : "text-slate-600"}`}>
            <div className="min-w-0">
              <p className="text-xs font-semibold truncate">{String(message.fileName || "Image").replace(/\.enc$/i, "")}</p>
              <p className="text-[11px] opacity-80">{formatFileSize(message.fileSize)}</p>
            </div>
            <button
              onClick={() => onDownload?.(message)}
              className={`p-2 rounded-lg transition ${isOwn ? "bg-white/10 text-white hover:bg-white/20" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
              aria-label="Download image"
            >
              <Download size={15} />
            </button>
          </div>
        </div>
      ) : isDocument ? (
        <div className={`max-w-md rounded-3xl border shadow-md ring-1 overflow-hidden ${isOwn ? "bg-slate-900 text-white border-slate-700 ring-white/5" : "bg-white border-slate-200 ring-slate-100"}`}>
          <div className={`flex items-center justify-between px-3 pt-3 ${isOwn ? "text-slate-300" : "text-slate-500"}`}>
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em]">{formatFileType(message.mimeType, message.fileName)}</span>
            <span className="text-[10px] font-medium">{isOwn ? "You sent" : "Received"}</span>
          </div>
          <div className="flex items-center gap-3 px-3 pb-3 pt-2">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${getFileIcon(message.mimeType).bg}`}>
              {React.createElement(getFileIcon(message.mimeType).icon, { size: 18, className: getFileIcon(message.mimeType).color })}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold truncate">{String(message.fileName || "Document").replace(/\.enc$/i, "")}</p>
              <p className={`text-xs mt-0.5 ${isOwn ? "text-slate-300" : "text-slate-500"}`}>
                {formatFileSize(message.fileSize)} · Encrypted file
              </p>
            </div>
            <button
              onClick={() => onDownload?.(message)}
              className={`p-2 rounded-lg transition ${isOwn ? "bg-white/10 text-white hover:bg-white/20" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
              aria-label="Download document"
            >
              <Download size={16} />
            </button>
          </div>
        </div>
      ) : (
        <div className={`max-w-[72%] px-4 py-2 rounded-2xl ${isOwn ? "bg-slate-900 text-white" : "bg-white border border-slate-200"}`}>
          <p className="text-sm">{message.text}</p>
          <p className={`text-[11px] mt-1 ${isOwn ? "text-slate-300" : "text-slate-500"}`}>
            {new Date(message.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </p>
        </div>
      )}
    </div>
  );
};

const ChatListItem = ({ chat, active, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full text-left p-3 rounded-lg transition-colors ${active ? "bg-blue-100" : "hover:bg-slate-100"}`}
  >
    <p className={`font-bold text-sm truncate ${active ? "text-blue-800" : "text-slate-900"}`}>{chat.name}</p>
    <p className={`text-xs truncate ${active ? "text-blue-700" : "text-slate-500"}`}>{chat.subtitle}</p>
  </button>
);

function currentLawyerId() {
  const raw = localStorage.getItem(sessionStorage.USER_KEY);
  if (!raw) return "";
  try {
    const user = JSON.parse(raw);
    return String(user?.id || "");
  } catch {
    return "";
  }
}

async function toClientMessage(serverMessage, currentUserId, privateKey) {
  const senderId = String(serverMessage.senderId || "");
  const isOwn = senderId === String(currentUserId || "");
  const wrappedSessionKey = isOwn
    ? serverMessage.senderEncryptedSessionKey || serverMessage.encryptedSessionKey
    : serverMessage.encryptedSessionKey;

  let text = "Unable to decrypt message";
  try {
    const sessionKey = await decryptSessionKey(wrappedSessionKey, privateKey);
    text = await decryptTextWithSessionKey(serverMessage.encryptedMessage, serverMessage.iv, sessionKey);
  } catch {
    text = "Encrypted message";
  }

  return {
    id: serverMessage._id,
    sender: isOwn ? "lawyer" : "client",
    text,
    messageType: serverMessage.messageType || "text",
    fileUrl: serverMessage.fileUrl || null,
    fileName: serverMessage.fileName || null,
    fileSize: serverMessage.fileSize || null,
    mimeType: serverMessage.mimeType || null,
    fileIv: serverMessage.fileIv || null,
    encryptedSessionKey: serverMessage.encryptedSessionKey || "",
    senderEncryptedSessionKey: serverMessage.senderEncryptedSessionKey || "",
    timestamp: serverMessage.createdAt || new Date().toISOString(),
  };
}

export const ChatPage = ({ navState }) => {
  const { bookings } = useBookings();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCaseId, setActiveCaseId] = useState(navState?.bookingId || "");
  const [mobileViewChat, setMobileViewChat] = useState(Boolean(navState?.bookingId || navState?.caseId));
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [keysReady, setKeysReady] = useState(false);
  const [receiverPublicKey, setReceiverPublicKey] = useState("");
  const [imagePreviewUrls, setImagePreviewUrls] = useState({});
  const [uploading, setUploading] = useState(false);
  
  const chatFileInputRef = useRef(null);

  const socketRef = useRef(null);
  const privateKeyRef = useRef("");
  const ownPublicKeyRef = useRef("");
  const imagePreviewUrlsRef = useRef({});

  const lawyerId = useMemo(() => currentLawyerId(), []);

  const chats = useMemo(
    () =>
      bookings
        .map((booking) => ({
          id: booking.caseId,
          name: booking.clientName || "Client",
          subtitle: `${booking.caseType || "Case"} · ${booking.date || ""} ${booking.time || ""}`,
          receiverId: String(booking.raw?.user?.id || booking.raw?.user_id || ""),
        }))
        .filter((item) => item.id && item.name.toLowerCase().includes(searchQuery.toLowerCase())),
    [bookings, searchQuery]
  );

  const activeChat = useMemo(() => chats.find((item) => String(item.id) === String(activeCaseId)) || chats[0], [chats, activeCaseId]);

  useEffect(() => {
    if (!activeChat && chats[0]) {
      setActiveCaseId(chats[0].id);
    }
  }, [activeChat, chats]);

  useEffect(() => {
    const initKeys = async () => {
      if (!lawyerId) return;
      try {
        const ownServerKey = await fetchPublicKey(lawyerId).catch(() => null);
        const ownKeys = await ensureUserKeyPair(lawyerId, ownServerKey?.publicKey || "", upsertOwnPublicKey);
        ownPublicKeyRef.current = ownKeys.publicKey;
        privateKeyRef.current = ownKeys.privateKey;
        setKeysReady(true);
      } catch {
        setKeysReady(false);
      }
    };

    initKeys().catch(() => {
      setKeysReady(false);
    });
  }, [lawyerId]);

  useEffect(() => {
    if (!activeChat?.id || !keysReady) return;

    const load = async () => {
      setLoading(true);
      try {
        const history = await fetchChatMessages(activeChat.id);
        const mapped = await Promise.all(history.map((item) => toClientMessage(item, lawyerId, privateKeyRef.current)));
        setMessages(mapped);
      } catch {
        setMessages([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [activeChat?.id, keysReady, lawyerId]);

  useEffect(() => {
    imagePreviewUrlsRef.current = imagePreviewUrls;
  }, [imagePreviewUrls]);

  useEffect(() => {
    return () => {
      Object.values(imagePreviewUrlsRef.current).forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch {
          // noop
        }
      });
    };
  }, []);

  useEffect(() => {
    if (!keysReady || !messages.length) return;

    let cancelled = false;

    const buildPreviews = async () => {
      const candidates = messages.filter(
        (message) =>
          message.messageType === "document" &&
          isImageDocument(message) &&
          message.fileUrl &&
          message.fileIv &&
          !imagePreviewUrlsRef.current[message.id]
      );

      for (const message of candidates) {
        if (cancelled) return;
        try {
          const token = localStorage.getItem(sessionStorage.TOKEN_KEY);
          const response = await fetch(`${API_BASE.replace(/\/api\/?$/, "")}${message.fileUrl}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            credentials: "include",
          });
          if (!response.ok) continue;

          const encryptedBuffer = await response.arrayBuffer();
          const wrappedSessionKey =
            message.sender === "lawyer"
              ? message.senderEncryptedSessionKey || message.encryptedSessionKey
              : message.encryptedSessionKey;
          if (!wrappedSessionKey) continue;

          const sessionKey = await decryptSessionKey(wrappedSessionKey, privateKeyRef.current);
          const decryptedBuffer = await decryptFileWithSessionKey(encryptedBuffer, message.fileIv, sessionKey);
          const blob = new Blob([decryptedBuffer], { type: message.mimeType || "image/jpeg" });
          const url = URL.createObjectURL(blob);

          if (!cancelled) {
            setImagePreviewUrls((prev) => ({ ...prev, [message.id]: url }));
          } else {
            URL.revokeObjectURL(url);
          }
        } catch {
          // noop
        }
      }
    };

    buildPreviews();

    return () => {
      cancelled = true;
    };
  }, [keysReady, messages]);

  useEffect(() => {
    if (!activeChat?.receiverId || !keysReady) return;

    fetchPublicKey(activeChat.receiverId)
      .then((response) => setReceiverPublicKey(response?.publicKey || ""))
      .catch(() => setReceiverPublicKey(""));
  }, [activeChat?.receiverId, keysReady]);

  useEffect(() => {
    if (!activeChat?.id || !keysReady) return;

    const token = localStorage.getItem(sessionStorage.TOKEN_KEY);
    if (!token) return;

    const socket = createChatSocket(token);
    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("chat:join-case", { caseId: activeChat.id });
    });

    socket.on("chat:new-message", async (serverMessage) => {
      if (String(serverMessage.caseId) !== String(activeChat.id)) return;
      const mapped = await toClientMessage(serverMessage, lawyerId, privateKeyRef.current);
      setMessages((prev) => (prev.some((msg) => String(msg.id) === String(mapped.id)) ? prev : [...prev, mapped]));
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [activeChat?.id, keysReady, lawyerId]);

  const sendMessage = async () => {
    if (!newMessage.trim() || !socketRef.current || !receiverPublicKey) return;

    try {
      const sessionKey = await createSessionKey();
      const encrypted = await encryptTextWithSessionKey(newMessage.trim(), sessionKey);
      const encryptedSessionKey = await encryptSessionKeyForPublicKey(sessionKey, receiverPublicKey);
      const senderEncryptedSessionKey = await encryptSessionKeyForPublicKey(sessionKey, ownPublicKeyRef.current);

      socketRef.current.emit("chat:send-message", {
        caseId: activeChat.id,
        encryptedMessage: encrypted.encryptedMessage,
        iv: encrypted.iv,
        encryptedSessionKey,
        senderEncryptedSessionKey,
        messageType: "text",
      });

      setNewMessage("");
    } catch {
      // noop
    }
  };

  const downloadDocument = async (doc) => {
    if (!doc?.fileUrl || !doc?.fileIv) return;

    try {
      const token = localStorage.getItem(sessionStorage.TOKEN_KEY);
      const response = await fetch(`${API_BASE.replace(/\/api\/?$/, "")}${doc.fileUrl}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!response.ok) throw new Error("Unable to download file");

      const encryptedBuffer = await response.arrayBuffer();
      const wrappedSessionKey = doc.sender === "lawyer" ? doc.senderEncryptedSessionKey || doc.encryptedSessionKey : doc.encryptedSessionKey;
      if (!wrappedSessionKey) throw new Error("Missing encrypted key metadata for this file");

      const sessionKey = await decryptSessionKey(wrappedSessionKey, privateKeyRef.current);
      const decryptedBuffer = await decryptFileWithSessionKey(encryptedBuffer, doc.fileIv, sessionKey);
      const blob = new Blob([decryptedBuffer], { type: doc.mimeType || "application/octet-stream" });
      const href = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = href;
      anchor.download = String(doc.fileName || "document").replace(/\.enc$/i, "");
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(href);
    } catch {
      // noop
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length || !keysReady) return;
    if (!receiverPublicKey) {
      window.alert("Client has not joined the secure chat yet.");
      event.target.value = "";
      return;
    }

    setUploading(true);
    try {
      for (const file of files) {
        const sessionKey = await createSessionKey();
        const encryptedText = await encryptTextWithSessionKey(`Encrypted document: ${file.name}`, sessionKey);
        const encryptedFile = await encryptFileWithSessionKey(file, sessionKey);
        const encryptedSessionKey = await encryptSessionKeyForPublicKey(sessionKey, receiverPublicKey);
        const senderEncryptedSessionKey = await encryptSessionKeyForPublicKey(sessionKey, ownPublicKeyRef.current);

        const encryptedUploadFile = new File([encryptedFile.encryptedFile], `${file.name}.enc`, {
          type: "application/octet-stream",
        });

        await uploadDocument(activeChat.id, encryptedUploadFile, {
          encryptedMessage: encryptedText.encryptedMessage,
          iv: encryptedText.iv,
          encryptedSessionKey,
          senderEncryptedSessionKey,
          fileIv: encryptedFile.fileIv,
          originalMimeType: file.type || "application/octet-stream",
        });
      }
    } catch (err) {
      window.alert(err?.message || "Failed to upload encrypted document");
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className={mobileViewChat ? "hidden md:block" : "block"}>
        <PageHeader title="Messages" subtitle="Real-time encrypted lawyer-client chat" showBack={false} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 sm:gap-5 h-[calc(100dvh-130px)] min-h-[500px] max-w-[1260px] mx-auto w-full flex-1">
        {/* Left list panel: visible on desktop, or on mobile when not viewing a chat */}
        <Card className={`md:col-span-4 flex flex-col p-0 overflow-hidden ${mobileViewChat ? "hidden md:flex" : "flex"}`}>
          <div className="p-3.5 sm:p-4 border-b shrink-0">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search chats..."
                className="pl-10"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {chats.map((chat) => (
              <ChatListItem
                key={chat.id}
                chat={chat}
                active={String(activeChat?.id) === String(chat.id)}
                onClick={() => {
                  setActiveCaseId(chat.id);
                  setMobileViewChat(true);
                }}
              />
            ))}
          </div>
        </Card>

        {/* Right chat panel: visible on desktop, or on mobile when viewing a chat */}
        <Card className={`md:col-span-8 flex flex-col p-0 overflow-hidden relative ${!mobileViewChat ? "hidden md:flex" : "flex"}`}>
          <div className="p-3.5 sm:p-4 border-b flex items-center gap-2.5 sm:gap-3 bg-white shrink-0">
            <button
              onClick={() => setMobileViewChat(false)}
              className="md:hidden w-10 h-10 rounded-xl bg-slate-50 border border-slate-200 text-slate-700 flex items-center justify-center shrink-0 hover:bg-slate-100 transition-colors"
              style={{ minWidth: "40px", minHeight: "40px" }}
              aria-label="Back to chat list"
            >
              <ArrowLeft size={18} />
            </button>
            <div className="min-w-0 flex-1">
              <h3 className="font-bold text-slate-900 truncate text-sm sm:text-base">{activeChat?.name || "Select a chat"}</h3>
              <p className="text-xs text-slate-500 truncate">{activeChat?.subtitle || "No active case selected"}</p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-2 sm:px-4 py-4 sm:py-5 bg-slate-50 min-h-0">
            {loading ? <p className="text-sm text-slate-500">Loading messages...</p> : null}
            {!receiverPublicKey && keysReady ? (
              <div className="mb-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Waiting for client secure key exchange...
              </div>
            ) : null}
            <div className="w-full px-1 sm:px-2 lg:px-6">
              <div className="flex justify-center mb-5 pt-1">
                <span className="bg-white text-slate-400 text-[10px] font-medium px-3 py-1 rounded-full shadow-sm border border-slate-100">
                  Today
                </span>
              </div>
              <div>
                {messages.map((message, index) => {
                  const previous = messages[index - 1];
                  const isNewGroup = !previous || previous.sender !== message.sender;

                  return (
                    <div key={message.id} className={isNewGroup ? "mt-3 first:mt-0" : "mt-1"}>
                      <MessageBubble
                        message={message}
                        isOwn={message.sender === "lawyer"}
                        onDownload={downloadDocument}
                        previewUrl={imagePreviewUrls[message.id]}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="px-3 sm:px-5 py-3 sm:py-4 border-t bg-white shrink-0 sticky bottom-0 z-20 safe-bottom">
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => chatFileInputRef.current?.click()}
                disabled={!receiverPublicKey || uploading}
                style={{ minWidth: "44px", minHeight: "44px" }}
                className="p-2.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition flex items-center justify-center shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Upload document"
              >
                <Paperclip size={20} />
              </button>
              <input
                type="file"
                ref={chatFileInputRef}
                onChange={handleFileUpload}
                className="hidden"
                multiple
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
              />
              <Input
                placeholder="Type a message..."
                className="flex-1 h-11 text-xs sm:text-sm"
                value={newMessage}
                onChange={(event) => setNewMessage(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && sendMessage()}
                disabled={!receiverPublicKey}
              />
              <Button className="h-11 px-4 text-xs sm:text-sm shrink-0" style={{ minWidth: "70px", minHeight: "44px" }} onClick={sendMessage} disabled={!newMessage.trim() || !receiverPublicKey}>
                <Send size={16} className="mr-1 sm:mr-1.5" /> Send
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
